import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { Badge, Button, Card, Col, Empty, Pagination, Row, Skeleton, Space, Spin, Tooltip, Typography } from "antd";
import { HolderOutlined, PlusOutlined } from "@ant-design/icons";
import { SortableElement, SortableHandle } from "react-sortable-hoc";
import { Localize } from "components/service";
import { useTranslation } from "components/use-hooks";
import { ErrorBlock, errorNotification, successNotification } from "components/request-result";
import { ORDER_BY, PAGE_SIZE, fetchOrderSnapshot, createOrderUpdates, nextRowOrder, findRecordPage } from "./settings-app-order";
import { createSettingsSortableContainer } from "./settings-app-sortable";

const variables = { first: PAGE_SIZE, page: 1, orderBy: ORDER_BY };
const SortableRecords = createSettingsSortableContainer(({ children }) => <div className="app-settings-records">{children}</div>);
const SortableRecord = SortableElement(({ children, visible, recordId }) => (
    <div className="app-settings-record" hidden={!visible} data-record-id={recordId}>{children}</div>
));
const HandleButton = SortableHandle(({ disabled, label }) => (
    <Button type="text" size="small" className="app-settings-drag-handle" disabled={disabled}
        aria-label={label} icon={<HolderOutlined />} />
));
const DragHandle = ({ disabled, label, visible }) => (
    <Tooltip title={label} open={visible ? undefined : false}>
        <span><HandleButton disabled={disabled} label={label} /></span>
    </Tooltip>
);
const validPage = (page) => Array.isArray(page?.data)
    && Number.isInteger(page?.paginatorInfo?.total) && page.paginatorInfo.total >= 0
    && Number.isInteger(page?.paginatorInfo?.lastPage) && page.paginatorInfo.lastPage >= 1
    && Number.isInteger(page?.paginatorInfo?.currentPage) && page.paginatorInfo.currentPage >= 1;

// This is a form cache, not the visible page order. Keep keys after a draft is saved.
export const mergeSettingsRows = (rows, records, model, removedIds = new Set()) => {
    const next = [...rows];
    records.filter((record) => record?.id != null).forEach((record) => {
        if (removedIds.has(String(record.id))) return;
        const index = next.findIndex((row) => row.record.id != null && String(row.record.id) === String(record.id));
        if (index < 0) next.push({ key: `${model}-${record.id}`, record });
        else next[index] = { ...next[index], record };
    });
    return next;
};

const SettingsAppSection = ({ model, query, orderQuery, useOrderMutation, entityProp, EditForm, title, emptyText, addText }) => {
    const client = useApolloClient();
    const translations = useTranslation();
    const { setOrder } = useOrderMutation();
    const { data, loading, error, refetch } = useQuery(query, {
        variables,
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
    });
    const [rows, setRows] = useState([]);
    const [pageInfo, setPageInfo] = useState(null);
    const [activeIds, setActiveIds] = useState([]);
    const [pageError, setPageError] = useState(null);
    const [loadingPage, setLoadingPage] = useState(false);
    const [sorting, setSorting] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [busyCount, setBusyCount] = useState(0);
    const initialized = useRef(false);
    const nextDraft = useRef(0);
    const removedIds = useRef(new Set());
    const currentPage = useRef(1);
    const generation = useRef(0);
    const mounted = useRef(true);
    const sectionRef = useRef(null);
    const sortLock = useRef(false);
    const sortSource = useRef(null);
    const busyRows = useRef(new Set());
    const reservedOrderMaximum = useRef(null);
    const pagePending = useRef(false);
    const translate = (key) => translations[key] || key;

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; generation.current += 1; };
    }, []);

    const commitPage = useCallback((page) => {
        const records = page.data.filter((record) => record?.id != null && !removedIds.current.has(String(record.id)));
        setRows((current) => mergeSettingsRows(current, records, model, removedIds.current));
        setActiveIds([...new Set(records.map((record) => String(record.id)))]);
        setPageInfo(page.paginatorInfo);
        currentPage.current = page.paginatorInfo.currentPage;
        initialized.current = true;
    }, [model]);

    useEffect(() => {
        if (!initialized.current && validPage(data?.[model])) commitPage(data[model]);
    }, [data, model, commitPage]);

    const queryPage = async (page) => {
        const result = await client.query({
            query,
            variables: { ...variables, page },
            fetchPolicy: "network-only",
            // Each structural mutation needs a snapshot taken after its own completion.
            context: { queryDeduplication: false },
        });
        if (!validPage(result.data?.[model])) throw new Error("APP_SETTINGS.Error_InvalidResponse");
        return result.data[model];
    };

    const loadPage = async (page, recordId = null) => {
        const requestGeneration = ++generation.current;
        pagePending.current = true;
        setLoadingPage(true);
        setPageError(null);
        try {
            let requestedPage = page;
            if (recordId != null) {
                const snapshot = await fetchOrderSnapshot(client, orderQuery, model);
                if (!mounted.current || requestGeneration !== generation.current) return false;
                requestedPage = findRecordPage(snapshot, recordId);
            }
            let result = await queryPage(requestedPage);
            if (!mounted.current || requestGeneration !== generation.current) return false;
            // Deletion can invalidate the last page, including while a request is in flight.
            while (result.paginatorInfo.lastPage < requestedPage) {
                if (!mounted.current || requestGeneration !== generation.current) return false;
                requestedPage = result.paginatorInfo.lastPage;
                result = await queryPage(requestedPage);
            }
            if (mounted.current && requestGeneration === generation.current) {
                commitPage(result);
                return true;
            }
        } catch (failure) {
            if (mounted.current && requestGeneration === generation.current) setPageError({ failure, page, recordId });
        } finally {
            if (mounted.current && requestGeneration === generation.current) {
                pagePending.current = false;
                setLoadingPage(false);
            }
        }
        return false;
    };

    const saveRow = async (key, record, wasDraft) => {
        setRows((current) => current
            .filter((row) => row.key === key || String(row.record.id) !== String(record.id))
            .map((row) => row.key === key ? { ...row, record } : row));
        if (wasDraft) {
            await loadPage(currentPage.current, record.id);
        }
    };

    const removeRow = (key, id) => {
        if (id != null && removedIds.current.has(String(id))) return;
        if (id != null) removedIds.current.add(String(id));
        setRows((current) => current.filter((row) => row.key !== key));
        setActiveIds((current) => current.filter((value) => value !== String(id)));
        if (id != null) {
            return loadPage(currentPage.current);
        }
    };

    const setRowBusy = (key, busy) => {
        if (busy) busyRows.current.add(key);
        else {
            busyRows.current.delete(key);
        }
        if (mounted.current) setBusyCount(busyRows.current.size);
    };

    const prepareCreate = async () => {
        if (sortLock.current) throw new Error(translate("APP_SETTINGS.Order_Saving"));
        try {
            const snapshot = await fetchOrderSnapshot(client, orderQuery, model);
            // Keep a high-water mark: a delayed snapshot may predate a completed local creation.
            const localMaximum = reservedOrderMaximum.current;
            const rowOrder = nextRowOrder(localMaximum == null ? snapshot : [...snapshot, { row_order: localMaximum }]);
            reservedOrderMaximum.current = rowOrder;
            return { row_order: rowOrder };
        } catch (failure) {
            throw new Error(translate(failure.message));
        }
    };

    const cannotSort = () => sortLock.current || loading || pagePending.current || busyRows.current.size > 0 || !!pageError;
    const startSort = ({ node, isKeySorting } = {}) => {
        sortSource.current = {
            ids: [...activeIds], page: currentPage.current,
            handle: isKeySorting ? node?.querySelector(".app-settings-drag-handle") : null,
        };
        sortLock.current = true;
        setSorting(true);
    };

    const finishSort = async ({ oldIndex, newIndex }) => {
        const source = sortSource.current;
        if (!source) return;
        let attemptedWrite = false;
        try {
            if (oldIndex === newIndex) return;
            setSavingOrder(true);
            const snapshot = await fetchOrderSnapshot(client, orderQuery, model);
            if (!mounted.current) return;
            const updates = createOrderUpdates(snapshot, source.ids, source.page, oldIndex, newIndex);
            for (const input of updates) {
                if (!mounted.current) return;
                attemptedWrite = true;
                await setOrder(input);
            }
            const refreshed = await loadPage(source.page);
            if (!mounted.current) return;
            if (refreshed) successNotification({ title: translate("APP_SETTINGS.Order_Saved"), description: translate("APP_SETTINGS.Order_Saved_Help") });
            else errorNotification({ label: translate("APP_SETTINGS.Error_Title"), reason: translate("APP_SETTINGS.Error_OrderRefresh") });
        } catch (failure) {
            if (!mounted.current) return;
            // The API has no transaction or rollback. Always recover from its actual order.
            await loadPage(source.page);
            if (!mounted.current) return;
            const reasonKey = attemptedWrite ? "APP_SETTINGS.Error_OrderPartial" : "APP_SETTINGS.Error_OrderChanged";
            const detail = failure.graphQLErrors?.[0]?.extensions?.reason || failure.message;
            errorNotification({ label: translate("APP_SETTINGS.Error_Title"), reason: `${translate(reasonKey)} ${detail !== reasonKey ? translate(detail || "") : ""}`.trim() });
        } finally {
            sortSource.current = null;
            sortLock.current = false;
            if (mounted.current) {
                setSorting(false);
                setSavingOrder(false);
                if (source.handle) requestAnimationFrame(() => {
                    // Do not steal focus if the administrator moved to the other section.
                    if (mounted.current && source.handle.isConnected && !source.handle.disabled
                        && !source.handle.closest(".app-settings-record")?.hidden
                        && (document.activeElement === document.body || document.activeElement === source.handle)) source.handle.focus();
                });
            }
        }
    };

    const cancelSort = (failure) => {
        sortSource.current = null;
        sortLock.current = false;
        if (mounted.current) {
            setSorting(false);
            setSavingOrder(false);
            if (failure) errorNotification({ label: translate("APP_SETTINGS.Error_Title"), reason: translate("APP_SETTINGS.Error_Retry") });
        }
    };

    const firstError = !initialized.current && (error || (!loading && !validPage(data?.[model])));
    const showError = (failure, retry) => (
        <ErrorBlock
            title={<Localize>APP_SETTINGS.Error_Title</Localize>}
            subtitle={<Localize>{failure?.message || "APP_SETTINGS.Error_InvalidResponse"}</Localize>}
            buttonText={<Localize>APP_SETTINGS.Retry</Localize>}
            action={retry}
        />
    );
    const pageIndexes = new Map(activeIds.map((id, index) => [id, index]));
    const rank = (row) => row.record.id == null ? -1 : (pageIndexes.get(String(row.record.id)) ?? Number.MAX_SAFE_INTEGER);
    // Reordering keyed children changes their position without unmounting their forms.
    const orderedRows = [...rows].sort((left, right) => rank(left) - rank(right));
    const hasDrafts = rows.some((row) => row.record.id == null);
    // Keep the focused handle enabled while a keyboard gesture is in progress.
    // The synchronous lock prevents starting another gesture; disable handles during writes.
    const sortingDisabled = savingOrder || loading || loadingPage || busyCount > 0 || !!pageError;

    return (
        <section ref={sectionRef} aria-labelledby={`${model}-heading`} aria-busy={loadingPage || sorting} className={`app-settings-section app-settings-${model}`}>
            <Card
                className="app-settings-section-card"
                title={<Space size={10}>
                    <Typography.Title level={4} id={`${model}-heading`}><Localize>{title}</Localize></Typography.Title>
                    {pageInfo && <Badge className="app-settings-count" count={pageInfo.total} overflowCount={99999} showZero />}
                    {(loadingPage || sorting) && <Spin size="small" />}
                </Space>}
                extra={<Button type="dashed" icon={<PlusOutlined />} disabled={!initialized.current || sorting} onClick={() => {
                    if (!sortLock.current) setRows((current) => [
                        { key: `${model}-draft-${++nextDraft.current}`, record: {} }, ...current,
                    ]);
                }}>
                    <Localize>{addText}</Localize>
                </Button>}
            >
                {!initialized.current && loading && <Skeleton active paragraph={{ rows: 3 }} />}
                {firstError && showError(error, () => refetch().catch(() => {}))}
                {initialized.current && <>
                    {savingOrder && <Typography.Paragraph role="status" className="app-settings-order-status"><Localize>APP_SETTINGS.Order_Saving</Localize></Typography.Paragraph>}
                    {!activeIds.length && !hasDrafts && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Localize>{emptyText}</Localize>} />}
                    {model === "games" && (activeIds.length > 0 || hasDrafts) && <div className="app-settings-game-columns" aria-hidden="true">
                        <Row gutter={12} align="middle" className="app-settings-game-row">
                            <Col className="app-settings-game-drag" />
                            <Col className="app-settings-game-title"><Localize>APP_SETTINGS.Label_Title</Localize></Col>
                            <Col className="app-settings-game-url"><Localize>APP_SETTINGS.Label_GameUrl</Localize></Col>
                            <Col className="app-settings-game-active"><Localize>APP_SETTINGS.Label_Active</Localize></Col>
                            <Col className="app-settings-game-actions" />
                        </Row>
                    </div>}
                    <SortableRecords useDragHandle axis="y" lockAxis="y" distance={4}
                        helperContainer={() => sectionRef.current} helperClass="app-settings-sort-helper"
                        shouldCancelStart={cannotSort} onSortStart={startSort} onSortEnd={finishSort} onSortCancel={cancelSort}>
                        {orderedRows.map((row, cacheIndex) => {
                            const visible = row.record.id == null || pageIndexes.has(String(row.record.id));
                            const savedOnPage = row.record.id != null && pageIndexes.has(String(row.record.id));
                            const handleDisabled = !savedOnPage || sortingDisabled || activeIds.length < 2;
                            return <SortableRecord key={row.key} visible={visible} recordId={row.record.id}
                                collection={savedOnPage ? "page" : "inactive"}
                                index={savedOnPage ? pageIndexes.get(String(row.record.id)) : cacheIndex}
                                disabled={handleDisabled}>
                                <EditForm
                                    {...{ [entityProp]: row.record }}
                                    autoFocus={row.record.id == null}
                                    visible={visible}
                                    disabled={sorting}
                                    onBusyChange={(busy) => setRowBusy(row.key, busy)}
                                    prepareCreate={prepareCreate}
                                    dragHandle={<DragHandle visible={visible} disabled={handleDisabled}
                                        label={translate(row.record.id == null ? "APP_SETTINGS.Sort_SaveFirst" : "APP_SETTINGS.Sort_Handle")} />}
                                    onSaved={(record) => saveRow(row.key, record, row.record.id == null)}
                                    onDeleted={(id) => removeRow(row.key, id)}
                                    onCancel={() => removeRow(row.key)}
                                />
                            </SortableRecord>;
                        })}
                    </SortableRecords>
                    {pageError && <div className="app-settings-page-error">
                        {showError(pageError.failure, () => loadPage(pageError.page, pageError.recordId))}
                    </div>}
                    <Pagination
                        className="app-settings-pagination"
                        current={pageInfo.currentPage}
                        total={pageInfo.total}
                        pageSize={PAGE_SIZE}
                        showSizeChanger={false}
                        hideOnSinglePage
                        disabled={sorting}
                        onChange={(page) => { if (!sortLock.current) loadPage(page); }}
                        locale={{
                            prev_page: translate("APP_SETTINGS.Previous_Page"), next_page: translate("APP_SETTINGS.Next_Page"),
                            prev_5: translate("APP_SETTINGS.Previous_Pages"), next_5: translate("APP_SETTINGS.Next_Pages"),
                            prev_3: translate("APP_SETTINGS.Previous_Pages"), next_3: translate("APP_SETTINGS.Next_Pages"),
                        }}
                    />
                </>}
            </Card>
        </section>
    );
};

export default SettingsAppSection;
